import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Clock, CheckCircle2, XCircle, Layers, Eye, Send, BadgeCheck, Repeat } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import OfficerShell, { STATUS } from "./OfficerShell";
import TehsildarDashboard from "./TehsildarDashboard";
import TehsilOfficeDashboard from "./TehsilOfficeDashboard";
import OfficerTabbedDashboard from "./OfficerTabbedDashboard";
import RdcDashboard from "./RdcDashboard";
import AroDashboard from "./AroDashboard";
import CircleDashboard from "./CircleDashboard";
import ResultTab from "./ResultTab";

const STAT_CARDS = [
  { key: "total", label: "एकूण अर्ज", Icon: Layers, cls: "text-[#1b5e3f] bg-emerald-50" },
  { key: "pending", label: "प्रलंबित", Icon: Clock, cls: "text-amber-700 bg-amber-50" },
  { key: "UNDER_VERIFICATION", label: "पडताळणीत", Icon: FileText, cls: "text-blue-700 bg-blue-50" },
  { key: "FORWARDED", label: "पुढे पाठवले", Icon: Send, cls: "text-purple-700 bg-purple-50" },
  { key: "APPROVED", label: "मंजूर", Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50" },
  { key: "ISSUED", label: "निर्गमित", Icon: BadgeCheck, cls: "text-[#1b5e3f] bg-emerald-50" },
  { key: "REJECTED", label: "नामंजूर", Icon: XCircle, cls: "text-red-700 bg-red-50" },
];

const TEH_LABEL_MAP = {
  Miraj: "मिरज", Sangli: "सांगली", Tasgaon: "तासगाव", Kavathemahankal: "कवठेमहांकाळ",
  Walva: "वाळवा", Ashta: "आष्टा", Shirala: "शिराळा", Kadegaon: "कडेगाव", Palus: "पलूस",
  Khanapur: "खानापूर", Atpadi: "आटपाडी", Jat: "जत", Sankh: "संख",
};

function CollectorRdcFlipCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.collectorRdcFlips().then((r) => { if (alive) setData(r); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const count = data?.count ?? 0;
  const items = data?.items || [];
  const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };

  return (
    <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="collector-rdc-flip-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><Repeat className="h-6 w-6" /></div>
        <div>
          <p className="text-[13px] font-semibold text-amber-800">RDC ने बदललेले (flip) निर्णय</p>
          <p className="text-[26px] font-bold leading-none text-amber-900" data-testid="collector-flip-count">{count}</p>
        </div>
        {count > 0 && (
          <button onClick={() => setOpen(!open)} data-testid="collector-flip-toggle"
            className="ml-auto rounded-full bg-amber-600 px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90">
            {open ? "लपवा" : "तपशील पहा"}
          </button>
        )}
      </div>

      {count === 0 && data && <p className="mt-2 text-[13px] text-amber-700">RDC ने अद्याप कोणताही अंतिम निर्णय बदललेला नाही.</p>}

      {open && count > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl bg-white p-1 ring-1 ring-amber-100">
          <table className="w-full text-[13px]" data-testid="collector-flip-table">
            <thead>
              <tr className="bg-amber-100/70 text-left text-amber-900">
                <th className="p-2.5 font-semibold">अ.क्र.</th>
                <th className="p-2.5 font-semibold">अर्ज क्र.</th>
                <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                <th className="p-2.5 font-semibold">तहसील</th>
                <th className="p-2.5 font-semibold">संस्था</th>
                <th className="p-2.5 font-semibold">बदल</th>
                <th className="p-2.5 font-semibold">कारण</th>
                <th className="p-2.5 font-semibold">दिनांक</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`collector-flip-row-${i}`}>
                  <td className="p-2.5">{i + 1}</td>
                  <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{r.applicationNumber}</td>
                  <td className="p-2.5">{r.name}</td>
                  <td className="p-2.5">{TEH_LABEL_MAP[r.tehsil] || r.tehsil}</td>
                  <td className="p-2.5">{r.localBody}</td>
                  <td className="p-2.5">
                    <span className="text-slate-400">{r.previousDecision || "—"}</span>
                    <span className="mx-1 text-slate-400">→</span>
                    <span className={`font-semibold ${r.currentDecision === "पात्र" ? "text-emerald-700" : "text-red-600"}`}>{r.currentDecision}</span>
                    {r.flipCount > 1 && <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[10.5px] font-bold text-amber-700">×{r.flipCount}</span>}
                  </td>
                  <td className="p-2.5 max-w-[220px] text-slate-600">{r.remark || "—"}</td>
                  <td className="p-2.5 whitespace-nowrap text-[12px] text-slate-500">{fmt(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CollectorDashboard() {
  const [view, setView] = useState("dash");
  const tabBtn = (k, label) => (
    <button onClick={() => setView(k)} data-testid={`collector-tab-${k}`}
      className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === k ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>
      {label}
    </button>
  );
  return (
    <OfficerShell>
      <div className="mb-5 flex flex-wrap gap-2">
        {tabBtn("dash", "डॅशबोर्ड")}
        {tabBtn("result", "निकाल")}
      </div>
      {view === "dash" && <><CollectorRdcFlipCard /><TehsilOfficeDashboard /></>}
      {view === "result" && <ResultTab canApprove={true} />}
    </OfficerShell>
  );
}


export default function OfficerDashboard() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [tehsil, setTehsil] = useState("");

  const load = useCallback(async () => {
    if (["tehsildar", "sdo", "rdc", "aro", "collector", "circle", "viewer"].includes(user?.role)) return;
    try {
      const [s, list] = await Promise.all([api.officerStats(), api.officerList(search, status, tehsil)]);
      setStats(s); setApps(list);
    } catch (e) { /* noop */ }
  }, [search, status, tehsil, user]);

  useEffect(() => { load(); }, [load]);

  if (user?.role === "tehsildar") return <TehsildarDashboard />;
  if (user?.role === "circle") return <CircleDashboard />;
  if (user?.role === "rdc") return <RdcDashboard />;
  if (user?.role === "viewer") return <RdcDashboard readOnly />;
  if (user?.role === "aro") return <AroDashboard />;
  if (user?.role === "sdo") return <OfficerTabbedDashboard role={user.role} />;
  if (user?.role === "collector") return <CollectorDashboard />;

  const totals = stats?.totals || {};
  const drill = stats?.drilldown || [];

  return (
    <OfficerShell>
      {/* stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {STAT_CARDS.map(({ key, label, Icon, cls }) => (
          <div key={key} className="rounded-2xl border border-[#e6dcc4] bg-white p-4">
            <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl ${cls}`}><Icon className="h-5 w-5" /></div>
            <p className="text-[24px] font-bold leading-none text-[#1b3d2a] font-latin">{totals[key] ?? 0}</p>
            <p className="mt-1 text-[12.5px] text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* drilldown for SDO / Collector / RDC */}
      {drill.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#e6dcc4] bg-white p-5">
          <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">तहसीलनिहाय आकडेवारी (ड्रिल-डाउन)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-2 pr-3">तहसील</th><th className="py-2 pr-3">उपविभाग</th>
                <th className="py-2 pr-3">एकूण</th><th className="py-2 pr-3">प्रलंबित</th>
                <th className="py-2 pr-3">मंजूर</th><th className="py-2 pr-3">निर्गमित</th><th className="py-2 pr-3">नामंजूर</th><th></th>
              </tr></thead>
              <tbody>
                {drill.map((r) => (
                  <tr key={r.tehsil} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-semibold text-[#1b3d2a]">{r.tehsil}</td>
                    <td className="py-2 pr-3 text-slate-500">{r.subdivision}</td>
                    <td className="py-2 pr-3">{r.total}</td>
                    <td className="py-2 pr-3 font-semibold text-amber-700">{r.pending}</td>
                    <td className="py-2 pr-3">{r.approved}</td>
                    <td className="py-2 pr-3">{r.issued}</td>
                    <td className="py-2 pr-3">{r.rejected}</td>
                    <td className="py-2"><button onClick={() => setTehsil(r.tehsil)} className="rounded-full border border-[#1b5e3f]/30 px-3 py-1 text-[12px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/5">पहा</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* applications list */}
      <div className="mt-6 rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <p className="text-[16px] font-bold text-[#1b5e3f]">अर्ज यादी</p>
          {tehsil && <span className="rounded-full bg-[#fdf7e5] px-3 py-1 text-[12.5px] font-semibold text-[#b8790a]">तहसील: {tehsil} <button onClick={() => setTehsil("")} className="ml-1 text-slate-500">✕</button></span>}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="नाव / अर्ज क्रमांक" className="w-56 rounded-full border border-slate-200 py-2 pl-9 pr-3 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]">
              <option value="">सर्व स्थिती</option>
              {Object.keys(STATUS).filter(k => k !== "DRAFT").map(k => <option key={k} value={k}>{STATUS[k].label}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead><tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="py-2.5 pr-3">अर्ज क्र.</th><th className="py-2.5 pr-3">अर्जदार</th><th className="py-2.5 pr-3">तहसील</th><th className="py-2.5 pr-3">स्थिती</th><th></th>
            </tr></thead>
            <tbody>
              {apps.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">कोणतेही अर्ज नाहीत</td></tr>}
              {apps.map((a) => {
                const st = STATUS[a.status] || STATUS.SUBMITTED;
                return (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="py-2.5 pr-3 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="py-2.5 pr-3">{a.data?.nameMr || a.applicantName}<br/><span className="text-[12px] text-slate-400">{a.data?.mobile}</span></td>
                    <td className="py-2.5 pr-3">{a.tehsil || "—"}</td>
                    <td className="py-2.5 pr-3"><span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${st.cls}`}>{st.label}</span></td>
                    <td className="py-2.5"><button onClick={() => navigate(`/officer/app/${a.id}`)} className="flex items-center gap-1 rounded-full bg-[#1b5e3f] px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33]"><Eye className="h-3.5 w-3.5" /> पहा</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </OfficerShell>
  );
}
